// Разбор адреса возврата единой формы входа (/login?return=<url>) и
// "отложенный возврат" в sessionStorage - переживает редирект на Google/
// Telegram и обратно, после чего App.vue завершает возврат, как только
// пользователь аутентифицирован (см. composables/useLoginReturn.ts).
// Финальное решение "куда можно" принимает бэкенд (allowlist
// BACKEND_AUTH_RETURN_ORIGINS) - здесь только не отправляем заведомый мусор.

export const PENDING_RETURN_KEY = "eco_pending_login_return";
export const HANDOFF_PARAM = "handoff_code";

export type ReturnTarget =
  | { kind: "internal"; path: string }
  // Тот же origin, но путь принадлежит не SPA, а другому upstream за edge-nginx
  // (/registrar/*): роутер увёл бы на /map, нужна полная навигация. Токены уже
  // в общем localStorage - handoff не нужен.
  | { kind: "hard"; url: string }
  | { kind: "external"; url: string }
  | { kind: "none" };

const NONE: ReturnTarget = { kind: "none" };
const LOCAL_HOSTS = new Set(["localhost", "127.0.0.1"]);

function isLoginPath(path: string): boolean {
  return /^\/login([/?#]|$)/.test(path);
}

function isForeignPath(path: string): boolean {
  return /^\/registrar([/?#]|$)/.test(path);
}

export function classifyReturn(raw: string | null | undefined, currentOrigin: string): ReturnTarget {
  if (!raw) return NONE;
  if (raw.startsWith("/")) {
    if (raw.startsWith("//") || raw.startsWith("/\\") || isLoginPath(raw)) return NONE;
    if (isForeignPath(raw)) return { kind: "hard", url: currentOrigin + raw };
    return { kind: "internal", path: raw };
  }
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return NONE;
  }
  const secure = url.protocol === "https:";
  const local = url.protocol === "http:" && LOCAL_HOSTS.has(url.hostname);
  if (!secure && !local) return NONE;
  if (url.username || url.password) return NONE;
  if (url.origin === currentOrigin) {
    const path = url.pathname + url.search + url.hash;
    if (isLoginPath(path)) return NONE;
    return isForeignPath(path) ? { kind: "hard", url: url.href } : { kind: "internal", path };
  }
  return { kind: "external", url: url.href };
}

export function appendHandoffCode(url: string, code: string): string {
  const u = new URL(url);
  u.searchParams.set(HANDOFF_PARAM, code);
  return u.href;
}

// Отложенный возврат живёт 10 минут: устаревшее значение (ушёл с /login кнопкой
// "назад", закрыл вкладку на полпути) не должно сработать при следующем входе.
const PENDING_TTL_MS = 10 * 60 * 1000;

type PendingStore = Pick<Storage, "getItem" | "setItem" | "removeItem">;

function defaultStore(): PendingStore | null {
  try {
    return sessionStorage;
  } catch {
    return null; // нет sessionStorage (node/SSR/приватный режим)
  }
}

export function stashPendingReturn(
  raw: string | null | undefined,
  now: number = Date.now(),
  store: PendingStore | null = defaultStore()
): void {
  try {
    if (store && raw) store.setItem(PENDING_RETURN_KEY, JSON.stringify({ url: raw, ts: now }));
  } catch {
    /* хранилище недоступно - возврат просто не сработает */
  }
}

export function peekPendingReturn(
  now: number = Date.now(),
  store: PendingStore | null = defaultStore()
): string | null {
  try {
    const stored = store?.getItem(PENDING_RETURN_KEY);
    if (!store || !stored) return null;
    const parsed = JSON.parse(stored);
    if (typeof parsed?.url !== "string" || typeof parsed?.ts !== "number" || now - parsed.ts > PENDING_TTL_MS) {
      store.removeItem(PENDING_RETURN_KEY);
      return null;
    }
    return parsed.url;
  } catch {
    return null;
  }
}

export function clearPendingReturn(store: PendingStore | null = defaultStore()): void {
  try {
    store?.removeItem(PENDING_RETURN_KEY);
  } catch {
    /* см. stashPendingReturn */
  }
}

export function takePendingReturn(
  now: number = Date.now(),
  store: PendingStore | null = defaultStore()
): string | null {
  const value = peekPendingReturn(now, store);
  clearPendingReturn(store);
  return value;
}
